/**
 * Merchant Initializer Component
 * Runs once on app load to restore/fetch merchant data
 * Rendered globally in layout to ensure merchant data is available on all pages
 */
import { useModel } from '@umijs/max';
import { useEffect, useRef } from 'react';
import { getActingAsMerchantsCache } from '@/models/actingAsMerchant';
import { resolveInitialMerchantId } from '@/models/merchant';

const MerchantInitializer: React.FC = () => {
  const {
    initialState,
    setInitialState,
    loading: initialStateLoading,
  } = useModel('@@initialState');
  const { merchants, loading, fetchMerchants, loadMerchants } =
    useModel('merchant');
  const initRef = useRef(false);

  useEffect(() => {
    const userId =
      initialState?.currentUser?.id || initialState?.currentUser?.userid;
    const role =
      initialState?.currentUser?.role || initialState?.currentUser?.access;

    // Only run once per user session when we have a user but no merchants loaded
    if (
      userId &&
      !initialStateLoading &&
      merchants.length === 0 &&
      !loading &&
      !initRef.current
    ) {
      initRef.current = true;

      // SuperAdmin / Admin in acting-as-owner mode: restore from the
      // sessionStorage cache written by enterMerchantOwnerMode. Neither role
      // may fall through to fetchMerchants — my-partners returns all partners
      // for SuperAdmin and empty for a grant-only Admin; their merchant
      // context only ever comes from entering an owner.
      if (role === 'SuperAdmin' || role === 'Admin') {
        const cached = getActingAsMerchantsCache();
        // Load even when the cache is empty (owner with no merchants yet): the
        // call is what seeds the model's userId, so a merchant created right
        // after this writes its user-scoped storage keys instead of unsuffixed
        // ones. enterMerchantOwnerMode seeds the same ref, but it is lost in the
        // full page reload that follows.
        // Resolve the selection before loadMerchants runs — it overwrites
        // aimerpay_merchant_{userId}, so hardcoding cached[0] here would both
        // land on the wrong merchant after F5 and destroy the saved id.
        loadMerchants(
          cached,
          String(userId),
          resolveInitialMerchantId(cached, String(userId)),
        );
        if (cached.length > 0) {
          setInitialState((prev: any) => ({ ...prev }));
        }
        return;
      }

      // Normal user: fetch from API
      fetchMerchants(userId).then(() => {
        // Trigger access re-evaluation by updating initialState
        setInitialState((prev: any) => ({ ...prev }));
      });
    }

    // Reset init flag when user changes (logout/login)
    if (!userId) {
      initRef.current = false;
    }
  }, [
    initialState?.currentUser,
    initialStateLoading,
    merchants.length,
    loading,
    fetchMerchants,
    loadMerchants,
  ]);

  // This component renders nothing
  return null;
};

export default MerchantInitializer;
